import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

// --- Global axios defaults ---
// Every request must send cookies (session_token, csrf_token) so the browser
// establishes an authenticated cross-site session. Never store JWTs in
// localStorage.
axios.defaults.withCredentials = true;

// Attach the CSRF token (read from the non-HttpOnly csrf_token cookie) on
// every unsafe request. The backend compares this to the cookie value using a
// constant-time comparison.
function readCookie(name) {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : '';
}

axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) {
      config.headers = { ...(config.headers || {}), 'X-CSRF-Token': csrf };
    }
  }
  return config;
});

const AuthContext = createContext();

export function AuthProvider({ children, callbackPending = false }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip the /me check; AuthCallback
    // exchanges session_id first and then triggers a refresh.
    if (callbackPending) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [callbackPending]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    // Discard tokens left by older clients; only HttpOnly cookies authenticate.
    try { localStorage.removeItem('paperpal_token'); } catch (_) { /* Storage may be disabled. */ }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Backend sets the HttpOnly session cookie + csrf cookie. The response
    // body no longer contains an access_token — user identity comes from /me.
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API}/auth/signup`, data);
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      // ignore — we still clear local state below
    }
    setUser(null);
  };

  const beginGoogle = useCallback(async ({ language, intent = 'signin', referralCode }) => {
    const response = await axios.post(`${API}/auth/google/start`, {
      origin: window.location.origin,
      language,
      intent,
      referral_code: referralCode || undefined,
    });
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/dashboard?oauth_state=${encodeURIComponent(response.data.state)}&google_intent=${intent}`;
    window.location.assign(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
  }, []);

  const exchangeSession = useCallback(async (sessionId, state) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId, state });
    const { user: userData } = response.data;
    setUser(userData);
    setLoading(false);
    return response.data;
  }, []);

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    beginGoogle,
    exchangeSession,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    isPaid: user?.is_paid || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
